/**
 * MessageTimestamp — the timestamp leaf for a chat transcript.
 *
 * A real `<time dateTime>`, so the machine-readable moment is in the DOM
 * whatever the visible label says, plus the full absolute moment carried on
 * BOTH `title` and `aria-label`. That pairing is the point of the component:
 * `title` alone puts the exact time behind a hover, which is unreachable on
 * touch, so the absolute rides the accessible name too and the short relative
 * label ("Just now", "14:32") is all that has to fit on screen.
 *
 * Presentation only. Formatting is a locale + timezone + live-clock problem the
 * consumer already owns — a transcript left open needs "Just now" to become
 * "2 min ago" without repainting the whole conversation, which is a ticking
 * subscription the host holds. So the consumer resolves both strings:
 *
 *   <MessageTimestamp
 *     dateTime={m.at}
 *     label={relative(m.at, now)}
 *     absoluteLabel={absolute(m.at)}
 *   />
 *
 * Renders NOTHING without a `dateTime` and a `label`. A still-streaming turn has
 * no persisted row yet, and an empty label must not leave a stray element behind
 * — a bubble reading as "avatar + timestamp, empty body" is the ghost-row
 * failure this guard exists to prevent.
 *
 * Type colour is `muted-foreground`, not a lighter tertiary ink. Measured on the
 * brand palette, the next step down lands under the 4.5:1 contrast floor for
 * 11px text. An always-visible timestamp is information a reader is meant to be
 * able to READ, not incidental decoration, so it does not get to sit under the
 * floor. `tabular-nums` keeps a ticking label from jittering its own width as
 * the digits change, and `select-none` keeps a drag-select of the conversation
 * copying the messages, not the furniture around them.
 */
import * as React from "react";
import { cn } from "../lib/cn";
import { Skeleton } from "../primitives/Skeleton";

export interface MessageTimestampProps extends Omit<
  React.TimeHTMLAttributes<HTMLTimeElement>,
  "children" | "title" | "dateTime"
> {
  /** ISO-8601 moment. Lands on `dateTime`; nullish renders nothing. */
  dateTime?: string | null;
  /** The short visible label ("Just now", "14:32"). Nullish renders nothing. */
  label?: string | null;
  /**
   * The full absolute moment ("12 March 2026 at 14:32"). Carried on `title` AND
   * `aria-label` so it is reachable without a hover. Falls back to `label`.
   */
  absoluteLabel?: string;
  /**
   * Hold the label's footprint with a placeholder while the row hydrates.
   * Distinct from the empty state: no `dateTime` means the turn has no
   * persisted row yet and NOTHING renders, while `loading` means the row
   * exists and its time is still arriving.
   */
  loading?: boolean;
}

export const MessageTimestamp = React.memo(
  React.forwardRef<HTMLTimeElement, MessageTimestampProps>(function MessageTimestamp(
    { dateTime, label, absoluteLabel, loading = false, className, ...props },
    ref,
  ) {
    if (loading) {
      return <Skeleton aria-hidden="true" className={cn("h-3 w-10 rounded-full", className)} />;
    }
    if (!dateTime || !label) return null;
    const absolute = absoluteLabel ?? label;
    return (
      <time
        ref={ref}
        dateTime={dateTime}
        title={absolute}
        aria-label={absolute}
        className={cn(
          "select-none text-[11px] font-medium leading-none tabular-nums text-muted-foreground",
          className,
        )}
        {...props}
      >
        {label}
      </time>
    );
  }),
);
